import { Timestamp, timestampDate, timestampFromDate } from '@bufbuild/protobuf/wkt'
import { useMemo } from 'react'

import { useGetCompany, useGetCompanyActivityLog } from '@/api/company.hook'
import { CompanyActivityLogItem, CompanyRiskChange } from '@/gen/inventory/v1/company_service_pb'
import { RiskLevel } from '@/gen/inventory/v1/risk_pb'

import { LEMA_USER_ID } from '@/const/contact'

import { isEngagementIrqUpdate } from '@/lib/risk/last-engagement-irq-change'

const isRiskChange = (
  item: CompanyActivityLogItem,
): item is CompanyActivityLogItem & {
  activityLogItem: { case: 'riskChange'; value: CompanyRiskChange }
} => item.activityLogItem.case === 'riskChange'

// Actor-less, source-less rows are Lema-internal; attributing to them reads as
// "never validated by a human" downstream (useIsIrqCompleted).
const isAttributableCompanyIrqUpdate = (item: CompanyActivityLogItem) =>
  item.activityLogItem.case === 'irqUpdate' && (!!item.userId || !!item.integrationSource)

const timeMs = (time: Timestamp | undefined) => (time ? timestampDate(time).getTime() : 0)

const sortByTimeDesc = <T extends CompanyActivityLogItem>(items: T[] | undefined) =>
  items?.sort((a, b) => timeMs(b?.time) - timeMs(a?.time))

export const useLastIrqChange = (companyId: string, engagementId?: string) => {
  const { data: activityLogData, isLoading } = useGetCompanyActivityLog(companyId)
  const { data: companyData } = useGetCompany(companyId, !engagementId)

  const companyRisk = companyData?.company?.validatedRisk ?? companyData?.company?.calculatedRisk
  const companyCalculatedRisk = companyData?.company?.calculatedRisk

  const lastIRQChange = useMemo(() => {
    if (!engagementId) {
      const sortedRiskChanges = sortByTimeDesc(activityLogData?.filter(isRiskChange))
      const latestUserRiskChange = sortedRiskChanges?.find((item) => item.userId)

      const canCompareRiskValues =
        companyRisk !== undefined &&
        companyCalculatedRisk !== undefined &&
        companyRisk !== RiskLevel.UNSPECIFIED &&
        companyCalculatedRisk !== RiskLevel.UNSPECIFIED

      const hasOverrideByValueDiff = canCompareRiskValues && companyRisk !== companyCalculatedRisk

      // Only fall back to log evidence when we can't compare values directly (e.g. calc still
      // pending). When values ARE comparable and equal, the override is not active — trusting a
      // stale riskChange entry (e.g. after the user reverted) would misattribute Lema-calculated
      // risk to a user.
      const hasOverrideByLog =
        !canCompareRiskValues &&
        latestUserRiskChange !== undefined &&
        companyRisk !== undefined &&
        companyRisk !== RiskLevel.UNSPECIFIED &&
        latestUserRiskChange.activityLogItem.value.newInherentRisk === companyRisk

      if (latestUserRiskChange && (hasOverrideByValueDiff || hasOverrideByLog)) {
        // A resubmit that doesn't move the tier writes no riskChange row, so the
        // irqUpdate is the only evidence it happened (ENG-3906).
        const latestIrqUpdate = sortByTimeDesc(
          activityLogData?.filter(isAttributableCompanyIrqUpdate),
        )?.[0]

        if (latestIrqUpdate && timeMs(latestIrqUpdate.time) > timeMs(latestUserRiskChange.time)) {
          return {
            userId: latestIrqUpdate.userId || LEMA_USER_ID,
            time: latestIrqUpdate.time,
            engagementId: undefined,
            integrationSource: latestIrqUpdate.integrationSource,
          }
        }

        return {
          userId: latestUserRiskChange.userId || LEMA_USER_ID,
          time: latestUserRiskChange.time,
          engagementId: undefined,
          integrationSource: latestUserRiskChange.integrationSource,
        }
      }
    }

    const irqLogs = activityLogData?.filter((item) => {
      if (engagementId) {
        return (
          isEngagementIrqUpdate(item) &&
          item.activityLogItem.value.orgCompanyEngagementId === engagementId
        )
      }

      return (
        item.activityLogItem.case === 'riskChange' ||
        item.activityLogItem.case === 'irqUpdate' ||
        isEngagementIrqUpdate(item)
      )
    })

    const sortedIrqLogs = sortByTimeDesc(irqLogs)

    const lastScorer = sortedIrqLogs?.[0]
    if (!lastScorer) {
      const time = activityLogData?.[0]?.time || timestampFromDate(new Date())
      return {
        userId: LEMA_USER_ID,
        time: time,
      }
    }

    return {
      userId: lastScorer?.userId || LEMA_USER_ID,
      time: lastScorer?.time,
      engagementId: isEngagementIrqUpdate(lastScorer)
        ? lastScorer.activityLogItem.value.orgCompanyEngagementId
        : undefined,
      integrationSource: lastScorer.integrationSource,
    }
  }, [activityLogData, engagementId, companyRisk, companyCalculatedRisk])

  return { lastIRQChange, isLoading }
}
